'use client'

import { useState, useRef, useCallback, useEffect } from 'react'
import { useRouter } from 'next/navigation'
import { MentionChip } from './MentionChip'
import { formatCents } from '@/lib/slash-commands/utils'
import { cn } from '@/lib/utils'

type CampaignDetails = {
  id: string
  name: string
  status: string
  budget: number | null
  workspace_id: string
}

type PopoverState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'loaded'; details: CampaignDetails }
  | { phase: 'error' }

type MentionPopoverProps = {
  id: string
  kind: string
  display: string
  stale?: boolean
}

function StatusBadge({ status }: { status: string }) {
  // Dark-mode pairing matches the table status pills — see
  // components/dashboard/CampaignsTable.tsx for the rationale.
  const colors: Record<string, string> = {
    active: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-500/15 dark:text-green-300 dark:border-green-500/30',
    paused: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-500/15 dark:text-yellow-300 dark:border-yellow-500/30',
    draft: 'bg-neutral-100 text-[#939294] border-neutral-200 dark:bg-white/10 dark:text-atrium-black/60 dark:border-atrium-border',
    funded: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-500/15 dark:text-blue-300 dark:border-blue-500/30',
    completed: 'bg-neutral-100 text-[#939294] border-neutral-200 dark:bg-white/10 dark:text-atrium-black/60 dark:border-atrium-border',
  }
  const cls = colors[status.toLowerCase()]
    ?? 'bg-neutral-100 text-[#939294] border-neutral-200 dark:bg-white/10 dark:text-atrium-black/60 dark:border-atrium-border'
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium', cls)}>
      {status}
    </span>
  )
}

function hrefForMention(kind: string, id: string): string | undefined {
  // Route map matches the resolvers' `href` values. Keep this in sync
  // with lib/mentions/resolvers/*.ts when adding new kinds.
  switch (kind) {
    case 'campaign': return `/campaigns/${id}`
    case 'video':    return `/videos/${id}`
    case 'brand':    return `/brand/${id}`
    default:         return undefined
  }
}

export function MentionPopover({ id, kind, display, stale }: MentionPopoverProps) {
  const router = useRouter()
  const [visible, setVisible] = useState(false)
  const [popover, setPopover] = useState<PopoverState>({ phase: 'idle' })
  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const href = hrefForMention(kind, id)

  // Rich hover popover (details panel + Open button) currently only
  // exists for campaigns — that's the only kind with a
  // `/api/campaigns/[id]/details` endpoint. Videos and brands get the
  // clickable chip but skip the hover fetch entirely. A future PR can
  // add details endpoints for the other kinds.
  const showRichPopover = kind === 'campaign'

  const fetchDetails = useCallback(async () => {
    if (popover.phase !== 'idle') return
    setPopover({ phase: 'loading' })
    try {
      const res = await fetch(`/api/campaigns/${id}/details`)
      if (!res.ok) { setPopover({ phase: 'error' }); return }
      const body = await res.json() as { campaign?: CampaignDetails }
      if (body.campaign) {
        setPopover({ phase: 'loaded', details: body.campaign })
      } else {
        setPopover({ phase: 'error' })
      }
    } catch {
      setPopover({ phase: 'error' })
    }
  }, [id, popover.phase])

  const handleMouseEnter = useCallback(() => {
    if (!showRichPopover) return
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null }
    hoverTimer.current = setTimeout(() => {
      setVisible(true)
      void fetchDetails()
    }, 250)
  }, [fetchDetails, showRichPopover])

  const handleMouseLeave = useCallback(() => {
    if (hoverTimer.current) { clearTimeout(hoverTimer.current); hoverTimer.current = null }
    leaveTimer.current = setTimeout(() => setVisible(false), 150)
  }, [])

  useEffect(() => () => {
    if (hoverTimer.current) clearTimeout(hoverTimer.current)
    if (leaveTimer.current) clearTimeout(leaveTimer.current)
  }, [])

  if (stale) {
    return (
      <span title="This resource has been deleted">
        <MentionChip display={display} kind={kind} stale />
      </span>
    )
  }

  return (
    <span className="relative inline-block">
      <span
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="inline-block"
      >
        <MentionChip
          display={display}
          kind={kind}
          onClick={href ? () => router.push(href) : undefined}
        />
      </span>

      {visible && (
        <span
          role="tooltip"
          onMouseEnter={handleMouseLeave.bind(null) /* cancel leave */}
          onMouseLeave={handleMouseLeave}
          className="absolute bottom-full left-0 z-50 mb-1.5 w-56 rounded-xl border border-atrium-border bg-atrium-inner shadow-lg ring-1 ring-atrium-black/5 overflow-hidden"
          style={{ pointerEvents: 'auto' }}
        >
          {popover.phase === 'loading' && (
            <span className="flex items-center justify-center py-4 text-xs text-[#939294]">
              Loading…
            </span>
          )}

          {popover.phase === 'error' && (
            <span className="flex items-center justify-center py-4 text-xs text-[#939294]">
              Could not load details.
            </span>
          )}

          {popover.phase === 'loaded' && (
            <span className="block">
              <span className="block border-b border-atrium-border px-3 py-2.5">
                <span className="block text-sm font-semibold text-atrium-black truncate">{popover.details.name}</span>
                <span className="mt-1 block">
                  <StatusBadge status={popover.details.status} />
                </span>
              </span>
              <span className="block px-3 py-2 text-xs text-[#939294]">
                {popover.details.budget != null && popover.details.budget > 0 ? (
                  <span className="block">Budget: {formatCents(popover.details.budget)}</span>
                ) : (
                  <span className="block">No budget set</span>
                )}
              </span>
              {href && (
                <span className="block border-t border-atrium-border px-3 py-2">
                  <button
                    type="button"
                    onClick={() => { router.push(href); setVisible(false) }}
                    className="w-full rounded-lg bg-accent-600 px-3 py-1.5 text-center text-xs font-semibold text-white hover:bg-accent-700 transition-colors"
                  >
                    Open campaign
                  </button>
                </span>
              )}
            </span>
          )}
        </span>
      )}
    </span>
  )
}
